import { sameAddress } from './address.js';

// The one selection model. Before this existed, each panel in the Tosefta site
// was driven by its own hand-rolled sync path (syncFooterTo, syncManuscriptTo,
// highlightNote...), which meant every new apparatus was another N wires to
// remember. Panels now subscribe here instead.
//
// `meta` carries *why* the selection changed, not just where it landed, which
// some layers legitimately need: a click on a synopsis row means "this word, in
// that witness", and the manuscript panel should switch witness tabs; the same
// word clicked in the body text means "this word, whichever witness you're
// already showing" and must not.
//
//   meta.source     string  id of the layer that originated it ('body',
//                           'synopsis', 'commentary', ...)
//   meta.witnessId  string  witness the click implies, when it implies one
//   meta.entryId    string  specific apparatus entry the reader aimed at

export function createSelection(initial = null) {
    let current = initial;
    let currentMeta = {};
    const subscribers = new Set();

    function emit() {
        // Copy first: a subscriber is allowed to unsubscribe (or subscribe)
        // while being notified.
        [...subscribers].forEach(fn => {
            try {
                fn(current, currentMeta);
            } catch (err) {
                // One broken layer must not stop the others from updating --
                // a half-synced set of panels is worse than one dead panel.
                console.error('[mvc] selection subscriber failed', err);
            }
        });
    }

    return {
        get() { return current; },
        get meta() { return currentMeta; },

        // `force` re-emits even when the address is unchanged, for the case
        // where only the meta moved (same word, different witness).
        set(addr, meta = {}, { force = false } = {}) {
            const metaChanged = JSON.stringify(meta) !== JSON.stringify(currentMeta);
            if (!force && !metaChanged && sameAddress(addr, current)) return;
            current = addr;
            currentMeta = meta;
            emit();
        },

        clear(meta = {}) {
            if (current == null) return;
            current = null;
            currentMeta = meta;
            emit();
        },

        subscribe(fn) {
            subscribers.add(fn);
            return () => subscribers.delete(fn);
        },
    };
}
